import { parseRef } from '@/lib/utils/reference-utils';
import { BIBLE_BOOK_CODES, type BibleBook } from '@/lib/utils/bible-utils';

/**
 * Find the passage ID that contains a given paragraph
 * @param paragraph - The paragraph to find the passage for
 * @param passages - Array of all passages
 * @returns The passage ID if found, null otherwise
 */
export function findPassageForParagraph(
    paragraph: TranslationParagraph,
    passages: Passage[]
): string | null {
    // Parse paragraph reference: "Gen:1:1:2:3" (book:chapterStart:verseStart:chapterEnd:verseEnd)
    const [bookCode, chapterStartStr, verseStartStr, chapterEndStr, verseEndStr] = paragraph.r.split(':');
    
    const paragraphStart = {
        chapter: parseInt(chapterStartStr, 10),
        verse: parseFloat(verseStartStr)
    };
    const paragraphEnd = {
        chapter: parseInt(chapterEndStr, 10),
        verse: parseFloat(verseEndStr)
    };

    // Find passages that overlap with this paragraph
    for (const passage of passages) {
        // Check if same book (passage.book is full name, bookCode is short code)
        const passageBookCode = BIBLE_BOOK_CODES[passage.book as BibleBook];
        if (passageBookCode !== bookCode) {
            continue;
        }

        const passageRef = parseRef(passage.ref);
        if (!passageRef) {
            continue;
        }

        // Check if paragraph overlaps with passage
        // Special case: verse 0 (titles/headers) should be considered part of the passage
        // that starts at verse 1 in the same chapter

        // Normalize verse 0 to verse 1 for overlap checking
        const normalizedParagraphStart = {
            chapter: paragraphStart.chapter,
            verse: paragraphStart.verse === 0 ? 1 : paragraphStart.verse
        };
        const normalizedParagraphEnd = {
            chapter: paragraphEnd.chapter,
            verse: paragraphEnd.verse === 0 ? 1 : paragraphEnd.verse
        };

        // Paragraph overlaps if:
        // 1. Paragraph starts before passage ends, AND
        // 2. Paragraph ends after passage starts

        const paragraphStartsBeforePassageEnds =
            normalizedParagraphStart.chapter < passageRef.chapterEnd ||
            (normalizedParagraphStart.chapter === passageRef.chapterEnd && normalizedParagraphStart.verse <= passageRef.verseEnd);

        const paragraphEndsAfterPassageStarts =
            normalizedParagraphEnd.chapter > passageRef.chapterStart ||
            (normalizedParagraphEnd.chapter === passageRef.chapterStart && normalizedParagraphEnd.verse >= passageRef.verseStart);

        if (paragraphStartsBeforePassageEnds && paragraphEndsAfterPassageStarts) {
            return passage.id;
        }
    }

    return null;
}


